/**
 * Anti-harcèlement du bandeau d'installation PWA (InstallAppNudge.tsx).
 *
 * Porté de memoneo (`frontend/src/lib/installNudge.ts`) : même parti pris —
 * la logique de décision est un module pur, testable sans monter de
 * composant ni toucher au DOM ; le composant se contente de lire/écrire
 * localStorage et d'appeler `shouldShowInstallNudge`.
 *
 * Règle : au plus 3 affichages au total, puis plus jamais ; entre deux
 * affichages, au moins 7 jours depuis le dernier rejet. On approxime
 * « affichage » par « rejet » — le bandeau ne peut être vu une deuxième fois
 * sans que l'utilisateur ait fermé le précédent, donc le nombre de rejets
 * enregistrés borne le nombre d'affichages passés.
 *
 * Contrairement à memoneo, il n'existe ici aucun signal serveur pour couper
 * l'affichage une fois l'app installée (pas de colonne `pwa_installed_at`
 * côté Chouette — voir InstallAppNudge.tsx) : le gate applicatif combine
 * cette fonction avec `isStandalonePwa()`, mais cette dernière ne fait pas
 * partie du module pur ci-dessous.
 */

const STORAGE_KEY = "chouette.installNudgeDismissals";

/** Rejets autorisés avant extinction définitive du bandeau pour cet appareil. */
export const MAX_INSTALL_NUDGE_SHOWS = 3;

/** Délai minimum avant réapparition après un rejet. */
export const INSTALL_NUDGE_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** Lit l'historique des rejets (timestamps ms) depuis localStorage.
 *  Silencieux sur tout accès impossible (Safari privé) ou contenu corrompu —
 *  un historique perdu redonne au pire quelques affichages en trop, jamais
 *  une erreur qui casse le rendu. */
export function loadInstallNudgeDismissals(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is number => typeof n === "number");
  } catch {
    return [];
  }
}

/** Enregistre un rejet (maintenant) et renvoie le nouvel historique — le
 *  composant appelle ceci puis re-render sur l'état renvoyé. */
export function recordInstallNudgeDismissal(now: number): number[] {
  const next = [...loadInstallNudgeDismissals(), now];
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Cosmétique : au pire le bandeau réapparaît une fois de trop.
  }
  return next;
}

/** Décide si le bandeau doit s'afficher, à partir d'un historique de rejets
 *  explicite — pure, sans lecture localStorage, pour rester facile à tester. */
export function shouldShowInstallNudge({
  now, dismissals,
}: { now: number; dismissals: number[] }): boolean {
  if (dismissals.length >= MAX_INSTALL_NUDGE_SHOWS) return false;
  if (dismissals.length === 0) return true;
  const lastDismissal = Math.max(...dismissals);
  return now - lastDismissal >= INSTALL_NUDGE_COOLDOWN_MS;
}

// ── Détection de plateforme (instructions manuelles quand pas de
//    beforeinstallprompt — iOS Safari notamment ne l'émet jamais) ──────────

export type InstallPlatform = "ios" | "android" | "desktop";

/** Pure — prend le user-agent (et le nombre de points tactiles) en paramètre
 *  plutôt que de lire `navigator` directement, pour rester testable sans
 *  jsdom UA-spoofing (ce dépôt n'a pas jsdom — voir installNudge.test.ts). */
export function detectInstallPlatform(
  userAgent: string,
  maxTouchPoints = 0,
): InstallPlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  // iPadOS 13+ ment : Safari s'y annonce avec un UA de Mac desktop. Sans ce
  // cas un iPad tomberait en "desktop", donc sur des instructions (« icône
  // dans la barre d'adresse ») qui n'existent pas chez lui — et il n'émet
  // pas non plus `beforeinstallprompt`. Un Mac réel n'ayant pas d'écran
  // tactile, `maxTouchPoints` sépare les deux sans ambiguïté.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
  return "desktop";
}
